import { Injectable, Injector, OnDestroy } from '@angular/core';
import { assert, Deferred, isDefined } from '@sama/js-core';
import { InjectableSuperclass } from '@sama/ng-core';
import { Subject, switchMap } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  AssetLoadError,
  determineAssetLoadErrorMessage,
} from '../../../cross-team-shared/misc-services/resources/url-request.service';
import { getUrlParam } from '../../../cross-team-shared/misc-utils';
import { SensorFrame2D } from '../../../cross-team-shared/state/sensor-renderer-state';
import { LoadTaskResult } from '../../../team-apis/content/content-connector';
import { EventTrackingValue } from '../../../team-apis/core/event-tracking';
import {
  ShellConnector,
  shellConnectorToken,
} from '../../../team-apis/core/shell-connector';
import { AbstractSensorContentStore2D } from '../state/abstract-sensor-content-store-2d';
import {
  AbstractPerFrameAssetLoadingService,
  LoadedFrameInfo,
} from './abstract-per-frame-asset-loading.service';

export interface AnnotationAsset {
  eventTrackingValue?: EventTrackingValue;
}

@Injectable()
export abstract class AbstractAssetInitializationService<
    AssetType extends AnnotationAsset = AnnotationAsset,
  >
  extends InjectableSuperclass
  implements OnDestroy
{
  protected shellConnector: ShellConnector;

  private loadTaskTrigger$ = new Subject<Deferred<LoadTaskResult>>();
  protected abstract assetType: string;
  protected abstract perFrameAssetLoadingService?: AbstractPerFrameAssetLoadingService<
    any,
    SensorFrame2D
  >;
  protected abstract store: AbstractSensorContentStore2D;

  constructor(injector: Injector) {
    super();
    this.shellConnector = injector.get(shellConnectorToken);

    this.setUpSubscriptions();
  }

  ngOnDestroy() {
    this.releaseAllocatedBlobUrls();
    this.store('sensorRendererState').delete();
  }

  loadTask() {
    this.releaseAllocatedBlobUrls();
    this.store('status').set('loading');

    // Undefined for image
    if (this.perFrameAssetLoadingService) {
      this.perFrameAssetLoadingService.reset$.next();
    }
    const deferred = new Deferred<LoadTaskResult>();
    this.loadTaskTrigger$.next(deferred);
    return deferred.promise;
  }

  /**
   * This should do all the asynchronous work needed for fetching assets,
   * without modifying the store. Another task may load before fetching is done,
   * so updating the store with info from these assets would be a bug.
   */
  protected startPerFrameLoading(_url: string): Promise<AssetType> {
    throw new Error('Method not implemented');
  }

  /**
   * This should do all the asynchronous work needed for fetching assets,
   * without modifying the store. Another task may load before fetching is done,
   * so updating the store with info from these assets would be a bug.
   */
  protected loadZipAsset(_url: string): Promise<AssetType> {
    throw new Error('Method not implemented');
  }

  protected updateStoreWithFrame(
    loadedFrame: LoadedFrameInfo<SensorFrame2D> | { error: Error },
  ) {
    if ('error' in loadedFrame) {
      this.handleAssetLoadFailure(loadedFrame.error);
      return;
    }

    const { frameNumber, sensorFrame } = loadedFrame;
    if (frameNumber === this.store.state().frameNumber) {
      // frame_number must be explicitly passed because the core team hasn't yet
      // added the frame_number to the context (if dealing with the first frame)
      this.shellConnector.trackEvent(
        {
          id: 'load_visible_frame',
          bucket: 'untracked',
          frame_number: frameNumber,
        },
        { isInteractive: false },
      );
    }

    this.store('sensorRendererState')('sensorFrames')(frameNumber).set(
      sensorFrame,
    );
    this.shellConnector.setFrameLoaded(frameNumber);
  }

  protected trackAssetLoadEventIfNotPfal(asset: AssetType, start: number) {
    const url = this.getInputUrl();
    assert(url, 'there must be a URL if it loaded successfully');
    if (!this.isPfalUrl(url)) {
      this.shellConnector.trackEvent(
        {
          id: 'load_asset',
          bucket: 'untracked',
          ...assetLoadEventValue(start, url),
          ...asset.eventTrackingValue,
        },
        { isInteractive: false },
      );
    }
  }

  protected handleAssetLoadFailure(error: any, assetLoadStart?: number) {
    let reason: string;
    if (error instanceof AssetLoadError) {
      reason = error.message;
    } else {
      reason = determineAssetLoadErrorMessage(error);
    }

    if (assetLoadStart) {
      this.trackAssetLoadFailureEvent(assetLoadStart, reason);
    }

    this.shellConnector.onAssetLoadError(reason);
  }

  protected getInputUrl() {
    return this.store.getInputUrl();
  }

  protected async loadAsset() {
    const url = this.getInputUrl();
    if (!url) {
      throw new AssetLoadError('No asset url given');
    } else if (this.isPfalUrl(url)) {
      return this.startPerFrameLoading(url);
    } else {
      return this.loadZipAsset(url);
    }
  }

  protected isPfalUrl(url: string) {
    return (
      url.startsWith(environment.assetServiceBaseUrl) ||
      !!getUrlParam('forcePfal')
    );
  }

  protected trackAssetLoadFailureEvent(assetLoadStart: number, reason: string) {
    const url = this.getInputUrl();
    if (!url || !this.isPfalUrl(url)) {
      this.shellConnector.trackEvent(
        {
          id: 'load_asset_failure',
          bucket: 'untracked',
          ...assetLoadEventValue(assetLoadStart, url),
          type: this.assetType,
          reason,
        },
        { isInteractive: false },
      );
    }
  }

  private releaseAllocatedBlobUrls() {
    const sensorFrames =
      // fix me if you change this code
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
      this.store.state().sensorRendererState?.sensorFrames || [];

    sensorFrames.filter(isDefined).forEach((frame) => this.releaseFrame(frame));
  }

  private setUpSubscriptions() {
    let start: number;
    this.subscribeTo(
      this.loadTaskTrigger$.pipe(
        switchMap(async (deferred) => {
          try {
            start = performance.now();
            const asset = await this.loadAsset();
            return { asset, deferred };
          } catch (error) {
            console.error(error);
            return { error };
          }
        }),
      ),
      (result) => {
        if ('error' in result) {
          this.handleAssetLoadFailure(result.error, start);
        } else {
          result.deferred.resolve(
            this.handleAssetLoadSuccess(result.asset, start),
          );
        }
      },
    );
  }

  protected abstract releaseFrame(sensorFrame: SensorFrame2D): void;

  protected abstract handleAssetLoadSuccess(
    asset: AssetType,
    start: number,
  ): LoadTaskResult;
}

function assetLoadEventValue(start: number, url: string | undefined) {
  const value: EventTrackingValue = { duration: performance.now() - start };
  if (url) {
    value.cacheable_url = url.startsWith('https://cacheable-client-assets');
  }
  return value;
}
